import { useQueryClient } from "@tanstack/react-query";

import { useInternationalization, useNotification } from "@/lib/contexts";
import API from "../../..";
import {
  AvantioAccommodationAlreadyLinkedError,
  GuestyListingAlreadyLinkedError,
  IcneaPropertyAlreadyLinkedError,
  UpdateRoomErpIntegrationsError,
} from "../../../rooms/errors";
import { customerServiceContractsByUserIdKey } from "../../customers-hooks/customers-query-keys";
import { useMutationWrapper } from "../../wrappers";
import { getRoomsWithIntegrationsKey } from "../get-rooms-with-integrations";
import { roomDetailsKey, roomUsedLicensesKey, updateRoomKey } from "../room-query-keys";

export interface UpdateRoomMutationParams {
  roomId: string;
  ownerId: string;
  handleSetInitalValues?: () => void;
  isCustomerOrManagerUser: boolean;
  onSuccessCallback?: () => void;
  onErrorCallback?: () => void;
}

export function useMutationUpdateRoom({
  roomId,
  ownerId,
  handleSetInitalValues,
  isCustomerOrManagerUser,
  onSuccessCallback,
  onErrorCallback,
}: UpdateRoomMutationParams) {
  const { t } = useInternationalization("pages.rooms.alerts.update-room");
  const { success, error } = useNotification();

  const queryClient = useQueryClient();

  const invalidateQueryKeys = async () => {
    await Promise.all(
      [
        roomDetailsKey(roomId),
        roomUsedLicensesKey(ownerId),
        customerServiceContractsByUserIdKey(ownerId),
        getRoomsWithIntegrationsKey(ownerId),
      ].map((key) => queryClient.invalidateQueries({ queryKey: key }))
    );

    // Also invalidate all rooms-with-integrations variants (different ownerId/self keys)
    await queryClient.invalidateQueries({ queryKey: ["rooms", "with-integrations"] });
  };

  const onError = async (err: Error) => {
    const isErpIntegrationsError = err instanceof UpdateRoomErpIntegrationsError;

    // Determine integration-specific conflict error message
    const getConflictMessage = (): string | null => {
      if (err instanceof GuestyListingAlreadyLinkedError) return t("guesty-listing-conflict");
      if (err instanceof AvantioAccommodationAlreadyLinkedError)
        return t("avantio-accommodation-conflict");
      if (err instanceof IcneaPropertyAlreadyLinkedError) return t("icnea-property-conflict");
      // Fallback for generic conflict errors
      const isConflictError =
        (err as any)?.statusCode === 409 ||
        err.message.includes("Conflict") ||
        err.message.includes("already linked");
      return isConflictError ? t("integration-conflict") : null;
    };

    const conflictMessage = getConflictMessage();

    if (!isCustomerOrManagerUser) {
      let errorMessage: string;
      if (conflictMessage) {
        errorMessage = conflictMessage;
      } else if (isErpIntegrationsError) {
        errorMessage = t("erp-integrations-error");
      } else {
        errorMessage = t("error");
      }
      error(errorMessage);
    } else {
      if (conflictMessage) {
        error(conflictMessage);
      } else if (!isErpIntegrationsError) {
        error(t("error"));
      } else {
        success(t("success"));
      }
    }

    if (isErpIntegrationsError) {
      handleSetInitalValues?.();
      await invalidateQueryKeys();
    }

    onErrorCallback?.();
  };

  const onSuccess = async () => {
    handleSetInitalValues?.();
    success(t("success"));
    await invalidateQueryKeys();
    onSuccessCallback?.();
  };

  const { mutate, isPending, isSuccess } = useMutationWrapper({
    mutationKey: updateRoomKey(roomId),
    mutationFn: API.Rooms.updateRoom,
    onSuccess,
    onError,
  });

  return {
    mutate,
    isPending,
    isSuccess,
  };
}
